import {
  examplePacks,
  installPack,
  markPacksOffered,
  packsOffered,
} from '@anytracker/core'
import { useCallback, useEffect, useState } from 'react'

import { Button } from './Button.js'
import { useToast } from './Toast.js'
import { useServices } from '../services/ServicesProvider.js'
import styles from './ExamplePackOffer.module.css'

/**
 * The first-run offer of ready-made tracks (T-108).
 *
 * Shared between Home and the track list rather than written twice, because
 * they are the two screens an empty database can start from and the offer has
 * to behave identically on both — the same packs, the same wording, and the
 * same "no thanks" that is never asked again.
 *
 * The installer knows nothing about what any pack contains. That is the whole
 * point of packs being ordinary user data: a pack about food and a pack about
 * anything else go through the same code, and this component could not tell you
 * which is which.
 */
export function ExamplePackOffer({
  onInstalled,
  onDeclined,
  mode = 'first-run',
}: {
  /** Called after tracks land, so the caller can reload. */
  readonly onInstalled: () => void | Promise<void>
  readonly onDeclined?: () => void
  /**
   * `first-run` is the offer: shown once, declinable, and never asked again.
   * `always` is the catalogue on the new-track screen — the same packs, minus
   * the question. Somebody who declined on day one and wants a starting point
   * on day thirty should not have to have said yes the first time.
   */
  readonly mode?: 'first-run' | 'always'
}) {
  const { storage, repos } = useServices()
  const { show } = useToast()

  const [offered, setOffered] = useState<boolean | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    void (async () => {
      setOffered(await packsOffered(storage))
    })()
  }, [storage])

  const install = useCallback(
    async (packId: string) => {
      const pack = examplePacks.find((candidate) => candidate.id === packId)
      if (!pack || busy) {
        return
      }
      setBusy(true)
      const result = await installPack(pack, repos)
      if (!result.ok) {
        setBusy(false)
        show({
          message: result.errors[0]?.message ?? 'Could not add those.',
          tone: 'danger',
        })
        return
      }
      await markPacksOffered(storage)
      setOffered(true)
      setBusy(false)
      show({ message: `Added ${String(result.value.tracks.length)} tracks.` })
      await onInstalled()
    },
    [busy, onInstalled, repos, show, storage],
  )

  const decline = useCallback(async () => {
    // Declining must leave the app completely empty and must not be asked
    // again (T-108).
    await markPacksOffered(storage)
    setOffered(true)
    onDeclined?.()
  }, [onDeclined, storage])

  // `null` while unread, so the offer never flashes up and vanishes for someone
  // who declined it months ago. The catalogue is not gated at all.
  if (mode === 'first-run' && offered !== false) {
    return null
  }

  return (
    <div className={styles['packs']}>
      {examplePacks.map((pack) => (
        <div key={pack.id} className={styles['pack']}>
          <span className={styles['packText']}>
            <span className={styles['packName']}>{pack.name}</span>
            <span className={styles['packDescription']}>{pack.description}</span>
          </span>
          <Button onClick={() => void install(pack.id)} disabled={busy}>
            Add
          </Button>
        </div>
      ))}
      {mode === 'first-run' && (
        <Button variant="ghost" onClick={() => void decline()}>
          No thanks, I will make my own
        </Button>
      )}
    </div>
  )
}

/**
 * Whether the offer would render anything, for a caller that needs to word its
 * empty state differently either side of it.
 */
export function useHasPackOffer(): boolean {
  const { storage } = useServices()
  const [offered, setOffered] = useState<boolean | null>(null)

  useEffect(() => {
    void (async () => {
      setOffered(await packsOffered(storage))
    })()
  }, [storage])

  return offered === false
}
